import { strIsNullOrEmpty } from '@fox/atoms';
import { CssBaseline, ThemeProvider as MUIThemeProvider, createTheme } from '@mui/material';
import React, { PropsWithChildren } from 'react';
import { StyleClasses } from './component-class-context';

const getClassStyles = (headerType: string): React.CSSProperties => {
	const headerClassStyle: React.CSSProperties = {};

	switch (headerType) {
		case 'h1-header':
			headerClassStyle.fontSize = '6rem';
			headerClassStyle.lineHeight = '7rem';
			headerClassStyle['@media only screen and (max-width: 767px)'] = {
				fontSize: '5rem', // ~16.7% down
				lineHeight: '5.875rem'
			};
			break;

		case 'h2-header':
			headerClassStyle.fontSize = '3.75rem';
			headerClassStyle.lineHeight = '4.5rem';
			headerClassStyle['@media only screen and (max-width: 767px)'] = {
				fontSize: '3.125rem', // ~16.7% down
				lineHeight: '3.75rem'
			};
			break;

		case 'h3-header':
			headerClassStyle.fontSize = '3rem';
			headerClassStyle.lineHeight = '3.5rem';
			headerClassStyle['@media only screen and (max-width: 767px)'] = {
				fontSize: '2.5rem', // ~16.7% down
				lineHeight: '2.875rem'
			};
			break;

		case 'h4-header':
			headerClassStyle.fontSize = '2.125rem';
			headerClassStyle.lineHeight = '3rem';
			headerClassStyle['@media only screen and (max-width: 767px)'] = {
				fontSize: '1.8125rem', // ~14.7% down
				lineHeight: '2.5rem'
			};
			break;

		case 'h5-header':
			headerClassStyle.fontSize = '1.5rem';
			headerClassStyle.lineHeight = '2rem';
			headerClassStyle['@media only screen and (max-width: 767px)'] = {
				fontSize: '1.25rem', // ~16.7% down
				lineHeight: '1.625rem'
			};
			break;

		case 'h6-header':
			headerClassStyle.fontSize = '1.25rem';
			headerClassStyle.lineHeight = '2rem';
			headerClassStyle['@media only screen and (max-width: 767px)'] = {
				fontSize: '1.125rem', // ~10% down
				lineHeight: '1.75rem'
			};
			break;

		case 'body1-header':
			headerClassStyle.fontSize = '1rem';
			headerClassStyle.lineHeight = '1.5rem';
			// no mobile override (body unchanged)
			break;

		case 'body2-header':
			headerClassStyle.fontSize = '0.875rem';
			headerClassStyle.lineHeight = '1.43rem';
			// no mobile override (body unchanged)
			break;
	}

	return headerClassStyle;
};

const getStyles = (
	stylingCustomisations: StylingCustomisations
): {
	buttonStyle: React.CSSProperties;
	buttonSecondaryStyle: React.CSSProperties;
	buttonTertiaryStyle: React.CSSProperties;
	headerStyle: React.CSSProperties;
	headerSubTextStyle: React.CSSProperties;
	buttonHoverStyle: React.CSSProperties;
	buttonSecondaryHoverStyle: React.CSSProperties;
	buttonTertiaryHoverStyle: React.CSSProperties;
	typographyStyle;
} => {
	const buttonStyle: React.CSSProperties = {};
	if (!strIsNullOrEmpty(stylingCustomisations.buttonBackgroundColour))
		buttonStyle.backgroundColor = stylingCustomisations.buttonBackgroundColour;
	if (stylingCustomisations.buttonFontSize > 0) buttonStyle.fontSize = `${stylingCustomisations.buttonFontSize}rem`;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonFont)) {
		buttonStyle.fontFamily = stylingCustomisations.buttonFont;
		if (stylingCustomisations.buttonFont.endsWith('-bold')) {
			buttonStyle.fontWeight = 700;
		}
	}
	if (!strIsNullOrEmpty(stylingCustomisations.buttonColour)) buttonStyle.color = stylingCustomisations.buttonColour;
	if (stylingCustomisations.buttonPaddingTop > 0) buttonStyle.paddingTop = `${stylingCustomisations.buttonPaddingTop}rem`;
	if (stylingCustomisations.buttonPaddingLeft > 0) buttonStyle.paddingLeft = `${stylingCustomisations.buttonPaddingLeft}rem`;
	if (stylingCustomisations.buttonPaddingRight > 0) buttonStyle.paddingRight = `${stylingCustomisations.buttonPaddingRight}rem`;
	if (stylingCustomisations.buttonPaddingBottom > 0) buttonStyle.paddingBottom = `${stylingCustomisations.buttonPaddingBottom}rem`;
	if (stylingCustomisations.buttonBorderWidth > 0) buttonStyle.borderWidth = `${stylingCustomisations.buttonBorderWidth}px`;
	if (stylingCustomisations.buttonBorderRadius > 0) buttonStyle.borderRadius = `${stylingCustomisations.buttonBorderRadius}px`;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonBorderColour)) buttonStyle.borderColor = stylingCustomisations.buttonBorderColour;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonBorderStyle)) buttonStyle.borderStyle = stylingCustomisations.buttonBorderStyle;

	const buttonSecondaryStyle: React.CSSProperties = {};
	if (!strIsNullOrEmpty(stylingCustomisations.buttonSecondaryBackgroundColour))
		buttonSecondaryStyle.backgroundColor = stylingCustomisations.buttonSecondaryBackgroundColour;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonSecondaryColour))
		buttonSecondaryStyle.color = stylingCustomisations.buttonSecondaryColour;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonSecondaryBorderColour))
		buttonSecondaryStyle.borderColor = stylingCustomisations.buttonSecondaryBorderColour;

	const buttonTertiaryStyle: React.CSSProperties = {};
	if (!strIsNullOrEmpty(stylingCustomisations.buttonTertiaryBackgroundColour))
		buttonTertiaryStyle.backgroundColor = stylingCustomisations.buttonTertiaryBackgroundColour;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonTertiaryColour))
		buttonTertiaryStyle.color = stylingCustomisations.buttonTertiaryColour;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonTertiaryBorderColour))
		buttonTertiaryStyle.borderColor = stylingCustomisations.buttonTertiaryBorderColour;

	const buttonHoverStyle: React.CSSProperties = {};
	if (!strIsNullOrEmpty(stylingCustomisations.buttonBorderHoverColour))
		buttonHoverStyle.borderColor = stylingCustomisations.buttonBorderHoverColour;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonHoverColour)) buttonHoverStyle.color = stylingCustomisations.buttonHoverColour;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonHoverBackgroundColour))
		buttonHoverStyle.backgroundColor = stylingCustomisations.buttonHoverBackgroundColour;

	const buttonSecondaryHoverStyle: React.CSSProperties = {};
	if (!strIsNullOrEmpty(stylingCustomisations.buttonSecondaryBorderHoverColour))
		buttonSecondaryHoverStyle.borderColor = stylingCustomisations.buttonSecondaryBorderHoverColour;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonSecondaryHoverColour))
		buttonSecondaryHoverStyle.color = stylingCustomisations.buttonSecondaryHoverColour;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonSecondaryHoverBackgroundColour))
		buttonSecondaryHoverStyle.backgroundColor = stylingCustomisations.buttonSecondaryHoverBackgroundColour;

	const buttonTertiaryHoverStyle: React.CSSProperties = {};
	if (!strIsNullOrEmpty(stylingCustomisations.buttonTertiaryBorderHoverColour))
		buttonTertiaryHoverStyle.borderColor = stylingCustomisations.buttonTertiaryBorderHoverColour;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonTertiaryHoverColour))
		buttonTertiaryHoverStyle.color = stylingCustomisations.buttonTertiaryHoverColour;
	if (!strIsNullOrEmpty(stylingCustomisations.buttonTertiaryHoverBackgroundColour))
		buttonTertiaryHoverStyle.backgroundColor = stylingCustomisations.buttonTertiaryHoverBackgroundColour;

	const typographyStyle = {};
	if (!strIsNullOrEmpty(stylingCustomisations.textFont)) {
		typographyStyle['fontFamily'] = stylingCustomisations.textFont;
		if (stylingCustomisations.textFont.endsWith('-bold')) {
			typographyStyle['fontWeight'] = 700;
		}
	}
	if (stylingCustomisations.textFontSize > 0) typographyStyle['fontSize'] = `${stylingCustomisations.textFontSize}rem`;

	const headerStyle: React.CSSProperties = {};
	if (!strIsNullOrEmpty(stylingCustomisations.headerFont)) {
		headerStyle.fontFamily = stylingCustomisations.headerFont;
		if (stylingCustomisations.headerFont.endsWith('bold')) {
			headerStyle.fontWeight = 700;
		}
	}
	if (!strIsNullOrEmpty(stylingCustomisations.headerFontColour)) headerStyle.color = stylingCustomisations.headerFontColour;
	const headerSubTextStyle: React.CSSProperties = {};
	if (!strIsNullOrEmpty(stylingCustomisations.headerSubtextFont)) {
		headerSubTextStyle.fontFamily = stylingCustomisations.headerSubtextFont;
		if (stylingCustomisations.headerSubtextFont.endsWith('bold')) {
			headerSubTextStyle.fontWeight = 700;
		}
	}
	if (!strIsNullOrEmpty(stylingCustomisations.headerSubtextFontColour))
		headerSubTextStyle.color = stylingCustomisations.headerSubtextFontColour;

	return {
		buttonStyle,
		headerStyle,
		headerSubTextStyle,
		buttonHoverStyle,
		typographyStyle,
		buttonSecondaryHoverStyle,
		buttonSecondaryStyle,
		buttonTertiaryHoverStyle,
		buttonTertiaryStyle
	};
};

export type StylingCustomisations = {
	buttonBackgroundColour: string;
	buttonBorderColour: string;
	buttonBorderHoverColour: string;
	buttonBorderRadius: number;
	buttonBorderStyle: string;
	buttonBorderWidth: number;
	buttonColour: string;
	buttonFont: string;
	buttonFontSize: number;
	buttonHoverBackgroundColour: string;
	buttonHoverColour: string;
	buttonSecondaryBackgroundColour: string;
	buttonSecondaryBorderColour: string;
	buttonSecondaryBorderHoverColour: string;
	buttonSecondaryColour: string;
	buttonSecondaryHoverBackgroundColour: string;
	buttonSecondaryHoverColour: string;
	buttonTertiaryBackgroundColour: string;
	buttonTertiaryBorderColour: string;
	buttonTertiaryBorderHoverColour: string;
	buttonTertiaryColour: string;
	buttonTertiaryHoverBackgroundColour: string;
	buttonTertiaryHoverColour: string;
	buttonPaddingBottom: number;
	buttonPaddingLeft: number;
	buttonPaddingRight: number;
	buttonPaddingTop: number;
	headerFont: string;
	headerFontColour: string;
	headerSubtextFont: string;
	headerSubtextFontColour: string;
	linkHoverColour: string;
	linkHoverUnderLine: boolean;
	textFont: string;
	textFontColour: string;
	textFontSize: number;
};

//we could make a case to have this come from umbraco backend settings?
const grey = {
	100: '#F1F2F4',
	200: '#E7E9EB',
	300: '#D7D9DB',
	400: '#B4B5B7',
	500: '#949597',
	600: '#6B6D6F',
	700: '#58595B',
	800: '#3A3B3C',
	900: '#191A1D'
};

const THEME = {
	mobileHeader: 'linear-gradient(90deg, #393f99, #265449)',
	colors: {
		primary: '#2F68D1',
		secondary: '#52649a',
		highlight: '#eb6321',
		good: '#4caa65',
		bad: '#d62c17',
		success: '#82AF49',
		neutral: '#3244b6',
		pageBackground: 'linear-gradient(180deg, rgba(65, 56, 182, 1), rgba(39, 87, 68, 1) 100%, rgba(39, 87, 68, 1))',
		white: '#ffffff',
		font: grey[700],
		darkGrey: grey[700],
		midGrey: grey[100],
		paleGrey: grey[100],
		blockGrey: grey[100],
		charcoal: grey[700],
		body: grey[500],
		sidebarBG: grey[700],
		topbarBG: grey[200],
		border: 'rgba(88, 89, 91, 0.5)',
		grey: grey
	},
	regular: 300,
	medium: 400,
	bold: 800,
	buttonTransition: 'all ease-in-out 0.3s',
	sidebarWidth: 50,
	padding: 20
};

export const ThemeProvider: React.FC<PropsWithChildren<{ stylingCustomisations: StylingCustomisations; stylingClasses: StyleClasses }>> = ({
	children,
	stylingCustomisations,
	stylingClasses
}) => {
	const {
		buttonHoverStyle,
		buttonStyle,
		headerStyle,
		typographyStyle,
		buttonSecondaryHoverStyle,
		buttonSecondaryStyle,
		buttonTertiaryStyle,
		buttonTertiaryHoverStyle
	} = getStyles(stylingCustomisations);

	const theme = createTheme({
		typography: {
			...typographyStyle,
			fontWeightMedium: 400,
			fontWeightBold: 800,
			fontWeightLight: 200,
			fontWeightRegular: 300,
			h1: { ...headerStyle, ...getClassStyles('h1-header') },
			h2: { ...headerStyle, ...getClassStyles('h2-header') },
			h3: { ...headerStyle, ...getClassStyles('h3-header') },
			h4: { ...headerStyle, ...getClassStyles('h4-header') },
			h5: { ...headerStyle, ...getClassStyles('h5-header') },
			h6: { ...headerStyle, ...getClassStyles('h6-header') },
			caption: { fontWeight: 400, fontSize: '0.75rem', lineHeight: 1.66 }
		},
		//links? for the hover
		palette: {
			text: {
				primary: stylingCustomisations.textFontColour || THEME.colors.font
			},
			primary: {
				main: THEME.colors.primary
			},
			secondary: {
				main: THEME.colors.secondary
			}
		},
		components: {
			MuiButton: {
				variants: [
					{
						props: { variant: 'contained' }, //primary
						style: {
							...buttonStyle,
							'&:hover': buttonHoverStyle,
							'&:disabled': { ...buttonStyle, opacity: 0.7 },
							textTransform: 'none'
						}
					},
					{
						props: { variant: 'outlined' }, //secondary
						style: {
							...buttonStyle,
							...buttonSecondaryStyle,
							'&:hover': buttonSecondaryHoverStyle,
							'&:disabled': { ...buttonSecondaryStyle, opacity: 0.7 },
							textTransform: 'none'
						}
					},
					{
						props: { variant: 'text' }, //tertiary
						style: {
							...buttonStyle,
							...buttonTertiaryStyle,
							'&:hover': buttonTertiaryHoverStyle,
							'&:disabled': { ...buttonTertiaryStyle, opacity: 0.7 },
							textTransform: 'none'
						}
					}
				]
			}
		},
		...THEME
	});

	return (
		<MUIThemeProvider theme={theme}>
			<CssBaseline /> {children}
		</MUIThemeProvider>
	);
};
